import { sourcePreviewKind } from "./sourcePreview";
import { bankingDiagnostic } from './banking/diagnostics';

const DATABASE_NAME = "invoice-studio-private-sources";
const DATABASE_VERSION = 1;
const STORE_NAME = "source-files";

const indexedDbRuntime = () => (typeof window === "undefined" ? null : window.indexedDB);

const openDatabase = () => new Promise((resolve, reject) => {
  const indexedDb = indexedDbRuntime();
  if (!indexedDb) {
    resolve(null);
    return;
  }
  const request = indexedDb.open(DATABASE_NAME, DATABASE_VERSION);
  request.onupgradeneeded = () => {
    if (!request.result.objectStoreNames.contains(STORE_NAME)) request.result.createObjectStore(STORE_NAME, { keyPath: "id" });
  };
  request.onsuccess = () => resolve(request.result);
  request.onerror = () => reject(request.error || new Error("Private source storage could not be opened."));
});

const runStoreRequest = async (mode, operation) => {
  const database = await openDatabase();
  if (!database) throw new Error('Private source storage is unavailable in this browser.');
  try {
    return await new Promise((resolve, reject) => {
      const transaction = database.transaction(STORE_NAME, mode);
      const request = operation(transaction.objectStore(STORE_NAME));
      let result;
      request.onsuccess = () => { result = request.result ?? true; };
      // A successful request can still be rolled back by the transaction.
      transaction.oncomplete = () => resolve(result);
      request.onerror = () => reject(request.error || new Error("Private source storage failed."));
      transaction.onabort = () => reject(transaction.error || new Error("Private source storage was interrupted."));
    });
  } finally {
    database.close();
  }
};

export const saveLocalSourceFile = async (intakeId, file) => {
  if (!intakeId || !file || !(file instanceof Blob)) return false;
  try {
    await runStoreRequest("readwrite", (store) => store.put({
      id: intakeId,
      blob: file,
      name: file.name || "source-document",
      type: file.type || "application/octet-stream",
      lastModified: Number(file.lastModified) || Date.now(),
      storedAt: new Date().toISOString(),
    }));
    return true;
  } catch (_error) {
    bankingDiagnostic('source-write-failed', _error);
    return false;
  }
};

export const loadLocalSourcePreview = async (intakeId) => {
  if (!intakeId || typeof URL === "undefined" || typeof URL.createObjectURL !== "function") return null;
  try {
    const record = await runStoreRequest("readonly", (store) => store.get(intakeId));
    if (!record?.blob) return null;
    const previewKind = sourcePreviewKind(record);
    if (previewKind === "metadata") return null;
    return {
      previewKind,
      previewUrl: URL.createObjectURL(record.blob),
      originalFilename: record.name,
    };
  } catch (_error) {
    bankingDiagnostic('source-preview-failed', _error);
    return null;
  }
};

export const loadLocalSourceFile = async (intakeId) => {
  if (!intakeId || typeof File === "undefined") return null;
  try {
    const record = await runStoreRequest("readonly", (store) => store.get(intakeId));
    if (!record?.blob) return null;
    return new File([record.blob], record.name || "source-document", {
      type: record.type || record.blob.type || "application/octet-stream",
      lastModified: Number(record.lastModified) || Date.now(),
    });
  } catch (_error) {
    bankingDiagnostic('source-read-failed', _error);
    return null;
  }
};

export const removeLocalSourceFile = async (intakeId) => {
  if (!intakeId) return false;
  try {
    await runStoreRequest("readwrite", (store) => store.delete(intakeId));
    return true;
  } catch (_error) {
    bankingDiagnostic('source-delete-failed', _error);
    return false;
  }
};
